// Paketler sol rail'i (AdminBranding 280 px kalıbı, DR15): görünür etiketli arama (DR17-4), etkin
// paketler sektöre göre gruplu (kanonik sözlük sırası, sektörsüz en sonda), satırda ad + gösterge
// sayısı + amber pill (kullanımdan kalkmış / eksik üye); arşiv kapalı "Arşiv (n)" grubunda.
// ≤ 1024 px: yerel `<select>` + sektör `<optgroup>` (aynı gruplama, aynı arama süzgeci).

import { useId, useState } from "react";
import { SECTOR_CODES, SECTOR_LABELS, type KpiPackage } from "@mahir/shared";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { foldSearch } from "../../lib/search-select";
import { PackageEmptyStates } from "./PackageEmptyStates";
import {
  decisionText,
  footerBtn,
  helpText,
  inputStyle,
  labelStyle,
  pillStyle,
  selectedRowBg,
  warningPill,
} from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY;

export interface PackageGroup {
  key: string;
  label: string;
  packages: KpiPackage[];
}

/** Arama süzgeci (ad / kod) + sektör gruplaması; arşiv ayrı. */
export function groupPackages(
  packages: readonly KpiPackage[],
  search: string,
): { groups: PackageGroup[]; archived: KpiPackage[]; visibleCount: number } {
  const q = foldSearch(search);
  const hit = (p: KpiPackage): boolean =>
    q === "" || foldSearch(p.name).includes(q) || foldSearch(p.code).includes(q);
  const shown = packages.filter(hit);
  const active = shown.filter((p) => p.status === "active");
  const groups: PackageGroup[] = [];
  for (const code of SECTOR_CODES) {
    const list = active.filter((p) => p.sectorCode === code);
    if (list.length > 0) groups.push({ key: code, label: SECTOR_LABELS[code], packages: list });
  }
  const known = new Set<string>(SECTOR_CODES);
  const rest = active.filter((p) => p.sectorCode === null || !known.has(p.sectorCode));
  if (rest.length > 0) groups.push({ key: "_none", label: C.page.sectorless, packages: rest });
  return {
    groups,
    archived: shown.filter((p) => p.status === "archived"),
    visibleCount: shown.length,
  };
}

export interface PackageRailProps {
  packages: readonly KpiPackage[];
  selectedId: string | null;
  search: string;
  onSearch: (s: string) => void;
  onSelect: (id: string) => void;
  onCreate: (() => void) | null;
  narrow: boolean;
}

/** Dar `<select>` seçeneği: pill yerine metin eki (kullanımdan kalktı / kısmi üye). */
export function optionLabel(pkg: KpiPackage, withCount: boolean): string {
  const parts = [pkg.name];
  if (withCount) parts.push(C.page.itemCount(pkg.itemCount));
  if (pkg.deprecatedCount > 0) parts.push(C.pills.deprecated);
  if (pkg.missingMemberCount > 0) parts.push(C.pills.partialMember);
  return parts.join(" · ");
}

function RowPills({ pkg }: { pkg: KpiPackage }): JSX.Element {
  return (
    <>
      {pkg.deprecatedCount > 0 ? (
        <span style={{ ...warningPill, marginLeft: 6 }}>{C.pills.deprecated}</span>
      ) : null}
      {pkg.missingMemberCount > 0 ? (
        <span style={{ ...warningPill, marginLeft: 6 }}>{C.pills.partialMember}</span>
      ) : null}
    </>
  );
}

export function PackageRail(p: PackageRailProps): JSX.Element {
  const searchId = useId();
  const selectId = useId();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const { groups, archived, visibleCount } = groupPackages(p.packages, p.search);
  const selectedArchived = archived.some((a) => a.id === p.selectedId);

  const search = (
    <div className="grid gap-1">
      <label htmlFor={searchId} style={labelStyle}>
        {C.page.search}
      </label>
      <input
        id={searchId}
        type="search"
        value={p.search}
        onChange={(e) => p.onSearch(e.target.value)}
        style={inputStyle}
      />
    </div>
  );
  const create = p.onCreate ? (
    <button type="button" style={footerBtn(true)} onClick={p.onCreate}>
      {C.page.newPackage}
    </button>
  ) : null;

  if (p.narrow) {
    return (
      <div className="grid gap-2" data-testid="package-rail-narrow">
        {search}
        <label htmlFor={selectId} style={labelStyle}>
          {C.page.rail}
        </label>
        <select
          id={selectId}
          value={p.selectedId ?? ""}
          onChange={(e) => {
            if (e.target.value) p.onSelect(e.target.value);
          }}
          style={{ ...inputStyle, minHeight: 44 }}
        >
          <option value="" disabled>
            {C.page.noSelection}
          </option>
          {groups.map((g) => (
            <optgroup key={g.key} label={g.label}>
              {g.packages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {optionLabel(pkg, true)}
                </option>
              ))}
            </optgroup>
          ))}
          {archived.length > 0 ? (
            <optgroup label={C.page.archiveGroup(archived.length)}>
              {archived.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {optionLabel(pkg, false)}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
        {visibleCount === 0 && p.packages.length > 0 ? (
          <PackageEmptyStates kind="noMatch" canManage={false} />
        ) : null}
        {create ? <div>{create}</div> : null}
      </div>
    );
  }

  const row = (pkg: KpiPackage): JSX.Element => {
    const on = pkg.id === p.selectedId;
    return (
      <li key={pkg.id}>
        <button
          type="button"
          aria-current={on ? "true" : undefined}
          onClick={() => p.onSelect(pkg.id)}
          className="flex w-full items-center gap-2 text-left"
          style={{
            ...decisionText,
            minHeight: 44,
            padding: "8px 12px",
            border: "none",
            borderRadius: 8,
            background: on ? selectedRowBg : "transparent",
            fontWeight: on ? 600 : 400,
            cursor: "pointer",
          }}
        >
          <span className="min-w-0 flex-1">
            {pkg.name}
            <RowPills pkg={pkg} />
          </span>
          <span style={{ ...pillStyle, whiteSpace: "nowrap" }}>{pkg.itemCount}</span>
        </button>
      </li>
    );
  };

  return (
    <nav aria-label={C.page.rail} className="grid gap-3" data-testid="package-rail">
      {create}
      {search}
      {visibleCount === 0 && p.packages.length > 0 ? (
        <PackageEmptyStates kind="noMatch" canManage={false} />
      ) : null}
      {groups.map((g) => (
        <section key={g.key} aria-label={g.label}>
          <h2 style={{ ...helpText, fontWeight: 600, margin: "0 0 4px 12px" }}>{g.label}</h2>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>{g.packages.map(row)}</ul>
        </section>
      ))}
      {archived.length > 0 ? (
        <section>
          <button
            type="button"
            aria-expanded={archiveOpen || selectedArchived}
            onClick={() => setArchiveOpen((v) => !v)}
            style={{
              ...helpText,
              fontWeight: 600,
              minHeight: 44,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              paddingLeft: 12,
            }}
          >
            {archiveOpen || selectedArchived ? "▾ " : "▸ "}
            {C.page.archiveGroup(archived.length)}
          </button>
          {archiveOpen || selectedArchived ? (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>{archived.map(row)}</ul>
          ) : null}
        </section>
      ) : null}
    </nav>
  );
}
